/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors'
import { typography } from '../../../shared/theme/typography';
import { normalizeSearchText } from '../../../shared/format/searchText';
import { styles } from '../posShared.styles';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import type { PosCheckoutCustomerOption } from '../../domain/posTypes';
import { formatPosMoney } from '../../application/posMoneyFormat';
import type { LoyaltyProgram } from '../../../loyalty/domain/loyaltyTypes';

type Props = {
  visible: boolean;
  customers: PosCheckoutCustomerOption[];
  customersTotal: number;
  customerPageIndex: number;
  customerPageSize: number;
  onLoadMoreCustomers: (pageIndex: number, loadAll?: boolean) => void;
  saleCustomerId: string | null;
  /** Hay paquetes en el carrito: el cliente es obligatorio (las sesiones quedan en su cuenta). */
  packageRequired?: boolean;
  /** Expande una vez cuando algo requiere cliente y no hay (ej. pago a cuenta). */
  forceExpanded?: boolean;
  /** Hay un pago «a cuenta» sin cliente: avisa por qué se abrió el selector. */
  creditRequired?: boolean;
  onSelectCustomer: (customerId: string | null) => void;
  onQuickAddCustomer: () => void;
  disabled?: boolean;
  loyaltyEnabled?: boolean;
  loyaltyBalance?: number;
  loyaltyProgram?: LoyaltyProgram | null;
  loyaltyRedeemPoints?: number;
  loyaltyRedeemDiscount?: number;
  onLoyaltyRedeemChange?: (points: number) => void;
};

const SEARCH_THRESHOLD = 4;

const TAX_CONDITION_LABELS: Record<number, string> = {
  1: 'Responsable Inscripto',
  2: 'Responsable Monotributo',
  3: 'Monotributista',
  4: 'Sujeto Exento',
  5: 'Consumidor Final',
  6: 'No Responsable',
};

function formatTaxConditionLabel(tc: number | null | undefined): string | null {
  if (tc == null) return null;
  return TAX_CONDITION_LABELS[tc] ?? null;
}

function formatCuitDisplay(cuit: string | null | undefined): string | null {
  const raw = (cuit ?? '').replace(/\D/g, '');
  if (raw.length !== 11) return cuit || null;
  return `${raw.slice(0, 2)}-${raw.slice(2, 10)}-${raw.slice(10)}`;
}

export function CheckoutSaleCustomerSelector({
  visible,
  customers,
  customersTotal,
  customerPageIndex,
  customerPageSize,
  onLoadMoreCustomers,
  saleCustomerId,
  packageRequired = false,
  forceExpanded = false,
  creditRequired = false,
  onSelectCustomer,
  onQuickAddCustomer,
  disabled = false,
  loyaltyEnabled = false,
  loyaltyBalance = 0,
  loyaltyProgram = null,
  loyaltyRedeemPoints = 0,
  loyaltyRedeemDiscount = 0,
  onLoyaltyRedeemChange,
}: Props) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!visible) {
      setSearch('');
      setExpanded(false);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && packageRequired && !saleCustomerId) setExpanded(true);
    // Solo al abrirse el checkout con paquete y sin cliente — no re-abrir si el usuario lo cierra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, packageRequired]);

  useEffect(() => {
    if (visible && forceExpanded && !saleCustomerId) setExpanded(true);
    // Igual que packageRequired: expande al pedirlo, pero si el usuario lo cierra no se re-abre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, forceExpanded]);

  useEffect(() => {
    if (search.trim() && customersTotal > customerPageSize) {
      onLoadMoreCustomers(0, true);
    }
  }, [search, customersTotal, customerPageSize, onLoadMoreCustomers]);

  const filtered = useMemo(() => {
    const q = normalizeSearchText(search);
    if (!q) return customers;
    return customers.filter((c) => normalizeSearchText(c.name).includes(q));
  }, [customers, search]);

  const showSearch = customers.length >= SEARCH_THRESHOLD;
  const totalPages = Math.ceil(customersTotal / customerPageSize);
  const selectedCustomer = customers.find((c) => c.id === saleCustomerId);
  const showLoyaltyInline =
    loyaltyEnabled && saleCustomerId != null && selectedCustomer != null && loyaltyBalance > 0;

  return (
    <View style={styles.checkoutSectionBlock}>
      <TouchableOpacity
        style={saleCustomerStyles.header}
        onPress={() => setExpanded((v) => !v)}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={expanded ? 'Ocultar selector de cliente' : 'Mostrar selector de cliente'}
        testID="pos.checkout.sale-customer-toggle"
      >
        <View style={saleCustomerStyles.headerLeft}>
          <Ionicons name="person-outline" size={18} color={ticketColors.ink2} />
          <Text style={saleCustomerStyles.headerTitle}>Cliente</Text>
        </View>
        <View style={saleCustomerStyles.headerRight}>
          {selectedCustomer && !expanded ? (
            <Text style={saleCustomerStyles.headerSummary} numberOfLines={1}>
              {selectedCustomer.name}
              {selectedCustomer.cuit ? ` · ${formatCuitDisplay(selectedCustomer.cuit)}` : ''}
            </Text>
          ) : !expanded ? (
            <Text style={saleCustomerStyles.headerPlaceholder}>Consumidor final</Text>
          ) : null}
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={18}
            color={ticketColors.ink3}
          />
        </View>
      </TouchableOpacity>

      {packageRequired && !saleCustomerId ? (
        <View style={saleCustomerStyles.packageHintRow} testID="pos.checkout.package-customer-hint">
          <Ionicons name="cube-outline" size={14} color={ticketColors.gold} />
          <Text style={saleCustomerStyles.packageHintText}>
            Elegí el cliente: las sesiones del paquete quedan en su cuenta.
          </Text>
        </View>
      ) : null}

      {creditRequired && !saleCustomerId ? (
        <View style={saleCustomerStyles.packageHintRow} testID="pos.checkout.credit-customer-hint">
          <Ionicons name="wallet-outline" size={14} color={ticketColors.gold} />
          <Text style={saleCustomerStyles.packageHintText}>
            Elegí el cliente: el pago a cuenta queda a su nombre.
          </Text>
        </View>
      ) : null}

      {expanded ? (
        <View style={saleCustomerStyles.body}>
          {showSearch ? (
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Buscar cliente…"
              placeholderTextColor={ticketColors.ink3}
              style={styles.checkoutCustomerSearch}
              autoCapitalize="words"
              autoCorrect={false}
              editable={!disabled}
              accessibilityLabel="Buscar cliente"
              testID="pos.checkout.sale-customer-search"
            />
          ) : null}

          {filtered.length === 0 ? (
            <Text style={styles.caption}>
              {search.trim()
                ? `Nadie coincide con «${search.trim()}».`
                : 'Todavía no tenés clientes cargados.'}
            </Text>
          ) : (
            <View style={modalExperience.listContainerPlain}>
              {filtered.map((customer) => {
                const selected = saleCustomerId === customer.id;
                return (
                  <TouchableOpacity
                    key={customer.id}
                    style={[modalExperience.pickerRow, selected && modalExperience.pickerRowActive]}
                    onPress={() => {
                      onSelectCustomer(customer.id);
                      setExpanded(false);
                    }}
                    disabled={customer.blocked || disabled}
                    accessibilityRole="button"
                    accessibilityLabel={`Cliente: ${customer.name}`}
                    accessibilityState={{ selected, disabled: customer.blocked }}
                  >
                    <View style={styles.checkoutCustomerPickerMain}>
                      <Text
                        style={[
                          modalExperience.pickerRowText,
                          selected && modalExperience.pickerRowTextActive,
                        ]}
                        numberOfLines={1}
                      >
                        {customer.name}
                      </Text>
                      {customer.cuit ? (
                        <Text
                          style={[
                            saleCustomerStyles.customerCuit,
                            selected && modalExperience.pickerRowTextActive,
                          ]}
                          numberOfLines={1}
                        >
                          {formatCuitDisplay(customer.cuit)}
                          {formatTaxConditionLabel(customer.taxCondition) ? ` · ${formatTaxConditionLabel(customer.taxCondition)}` : ''}
                        </Text>
                      ) : null}
                      {customer.blocked ? <StatusPill label="Bloqueado" tone="danger" /> : null}
                    </View>
                    <Text
                      style={[
                        styles.checkoutCustomerPickerBalance,
                        selected && modalExperience.pickerRowTextActive,
                      ]}
                    >
                      {formatPosMoney(customer.balance)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {totalPages > 1 && !search.trim() ? (
            <View style={styles.checkoutCustomerPagination}>
              <TouchableOpacity
                style={[
                  styles.checkoutCustomerPaginationButton,
                  customerPageIndex === 0 && styles.checkoutCustomerPaginationButtonDisabled,
                ]}
                onPress={() => onLoadMoreCustomers(customerPageIndex - 1)}
                disabled={customerPageIndex === 0}
                accessibilityRole="button"
                accessibilityLabel="Página anterior"
              >
                <Text
                  style={[
                    styles.checkoutCustomerPaginationButtonText,
                    customerPageIndex === 0 && styles.checkoutCustomerPaginationButtonTextDisabled,
                  ]}
                >
                  Anterior
                </Text>
              </TouchableOpacity>
              <Text style={styles.checkoutCustomerPaginationInfo}>
                {customerPageIndex + 1} de {totalPages}
              </Text>
              <TouchableOpacity
                style={[
                  styles.checkoutCustomerPaginationButton,
                  customerPageIndex === totalPages - 1 && styles.checkoutCustomerPaginationButtonDisabled,
                ]}
                onPress={() => onLoadMoreCustomers(customerPageIndex + 1)}
                disabled={customerPageIndex === totalPages - 1}
                accessibilityRole="button"
                accessibilityLabel="Página siguiente"
              >
                <Text
                  style={[
                    styles.checkoutCustomerPaginationButtonText,
                    customerPageIndex === totalPages - 1 && styles.checkoutCustomerPaginationButtonTextDisabled,
                  ]}
                >
                  Siguiente
                </Text>
              </TouchableOpacity>
            </View>
          ) : null}

          <TouchableOpacity
            style={saleCustomerStyles.quickAddBtn}
            onPress={onQuickAddCustomer}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel="Crear cliente nuevo"
            testID="pos.checkout.sale-customer-quick-add"
          >
            <Ionicons name="add-circle-outline" size={16} color={ticketColors.gold} />
            <Text style={saleCustomerStyles.quickAddBtnText}>Crear cliente nuevo</Text>
          </TouchableOpacity>

          {saleCustomerId ? (
            <TouchableOpacity
              onPress={() => onSelectCustomer(null)}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityLabel="Quitar cliente de la venta"
            >
              <Text style={saleCustomerStyles.clearBtn}>Quitar cliente</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {showLoyaltyInline ? (
        <View style={saleCustomerStyles.loyaltyPanel} testID="pos.checkout.loyalty-inline">
          <View style={saleCustomerStyles.loyaltyHeader}>
            <Text style={saleCustomerStyles.loyaltyTitle}>
              Fidelización · {loyaltyBalance} pts
            </Text>
            {loyaltyRedeemDiscount > 0 ? (
              <Text style={saleCustomerStyles.loyaltyDiscount}>
                Dto: {formatPosMoney(loyaltyRedeemDiscount)}
              </Text>
            ) : null}
          </View>
          {loyaltyProgram ? (
            <TextInput
              style={saleCustomerStyles.loyaltyInput}
              keyboardType="number-pad"
              value={loyaltyRedeemPoints > 0 ? String(loyaltyRedeemPoints) : ''}
              onChangeText={(t) => onLoyaltyRedeemChange?.(Number(t.replace(/[^\d]/g, '') || 0))}
              placeholder={`Canjear pts (mín ${loyaltyProgram.minRedeemPoints})`}
              placeholderTextColor={ticketColors.ink3}
              editable={!disabled}
              testID="pos.checkout.loyalty-redeem-input"
            />
          ) : (
            <Text style={saleCustomerStyles.loyaltyMeta}>No hay programa activo.</Text>
          )}
        </View>
      ) : null}
    </View>
  );
}

const saleCustomerStyles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    ...typography.body,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  headerRight: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    minWidth: 0,
  },
  headerSummary: {
    flexShrink: 1,
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  headerPlaceholder: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  packageHintRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingBottom: spacing.xs,
  },
  packageHintText: {
    flex: 1,
    fontSize: 12,
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  customerCuit: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    marginTop: 2,
  },
  body: {
    gap: spacing.sm,
    paddingBottom: spacing.xs,
  },
  quickAddBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: ticketColors.gold,
    borderRadius: 10,
    backgroundColor: ticketColors.brandSoft,
  },
  quickAddBtnText: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  clearBtn: {
    ...typography.caption,
    color: ticketColors.ink2,
    textDecorationLine: 'underline',
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  loyaltyPanel: {
    marginTop: spacing.xs,
    padding: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
    gap: 6,
  },
  loyaltyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  loyaltyTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  loyaltyDiscount: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ok,
    fontFamily: ticketFonts.sans,
  },
  loyaltyInput: {
    borderWidth: 1,
    borderColor: ticketColors.gold,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper,
    fontFamily: ticketFonts.sans,
  },
  loyaltyMeta: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
});
